import { IGameState } from "State/Game/IGameState";
import Element from "../Element";
import Renderable from "Component/Renderable";
import { getCurrentTractorPosition } from "System/UI/SeasonPass/TractorHelper";
import { MathHelper } from "Utility/MathHelper";
import TransformComponent from "Component/Transform";
import { SeasonPassRankProgress } from "Layouts/Quests/SeasonPass/SeasonPassRankProgress";
import { finishedSeasonPass } from "State/Player/PlayerSeasonPassHelper";
import { IPlayerState } from "State/Player/IPlayerState";
import { StartAnimation, StartAnimationClip } from "System/Animation/AnimationHelper";
import Armature from "Prototype/Armature";
import GameObject from "GameObject/GameObject";
import { DragonbonesDefault, DragonbonesFadeIn } from "System/Animation/Clip";
import { Point } from "pixi.js";

export const PROGRESS_BAR_RANK_WIDTH = 160;

type SeasonPassRankProgressItemProps = {
    state: IGameState;
};

export class SeasonPassRankProgressItem extends Element {
    private state: IGameState;
    private layout: any;
    private tractorArmature: Armature;
    private animating: boolean = false;

    public create(props: SeasonPassRankProgressItemProps): void {
        super.create({
            type: 'group',
            layout: {
                anchors: [0, 0, 0, 0],
                pivot: [0, 0],
                offsets: [0, 0, 0, 0],
            },
        });

        this.state = props.state;
        this.layout = new SeasonPassRankProgress();
        this.addChild(this.layout.root);

        this.tractorArmature = GameObject.Instantiate(Armature, {
            dragonbonesName: 'filling_progress_bar',
            armatureName: 'tractor',
            alpha: 1,
            playOnStart: false,
            position: new Point(140, -20),
        });
        this.layout.Tractor.addChild(this.tractorArmature);
        StartAnimationClip(
            new DragonbonesDefault(this.tractorArmature, {
                animationName: 'tractor_idle',
            })
        );

        this.init();
    }

    public init(): void {
        const fill = this.layout.ProgressBarFill;
        const currentPosition = getCurrentTractorPosition(this.state.playerState, true);

        // Set bar fill length
        if (finishedSeasonPass(this.state.playerState, true)) {
            fill.getComponent(TransformComponent).width = (this.state.playerState.seasonPass.ranks.length - 1) * PROGRESS_BAR_RANK_WIDTH;
        } else {
            const percProgress = MathHelper.normalize(currentPosition.progress, 0, this.state.playerState.seasonPass.ranks[currentPosition.rank]?.neededProgress);
            fill.getComponent(TransformComponent).width = Math.min(
                MathHelper.lerp(0, PROGRESS_BAR_RANK_WIDTH, percProgress),
                PROGRESS_BAR_RANK_WIDTH
            ) + ((currentPosition.rank - 1) * PROGRESS_BAR_RANK_WIDTH);
        }

        this.updateTractorPosition();
    }

    public updateLayout(state: IGameState) {
        this.state = state;
        this.init();
    }

    public updateTractorPosition(): void {
        const currentPosition = getCurrentTractorPosition(this.state.playerState, true);
        const neededProgress = this.state.playerState.seasonPass.ranks[currentPosition.rank]?.neededProgress;
        const progress = currentPosition.progress / neededProgress;
        const barWidth = progress * PROGRESS_BAR_RANK_WIDTH;
        this.layout.Tractor.getComponent(TransformComponent).x = barWidth + ((currentPosition.rank - 1) * PROGRESS_BAR_RANK_WIDTH);
    }

    public destroy() {
        super.destroy();
    }

    public animateRankProgress(playerState: IPlayerState, progress: number, rankCompleteCallback: (rank: number) => void, completeAnimationCallback: () => void) {
        const ranks = playerState.seasonPass?.ranks || [];
        const currentRank = getCurrentTractorPosition(playerState, true).rank;
        let oldProgress = getCurrentTractorPosition(playerState, true).progress;
        this.animating = true;

        let firstAnimationStep = true;

        let remainingProgress = oldProgress + progress;
        let rank = currentRank;

        const getTarget = (rank: number) => {
            return ranks[rank]?.neededProgress || 0;
        }

        const createAnimationStep = (
            rank: number,
            endProgress: number,
            onComplete: () => void
        ) => {
            const target = getTarget(rank);
            const percProgressEnd = MathHelper.normalize(endProgress, 0, target);
            const width = Math.min(
                MathHelper.lerp(0, PROGRESS_BAR_RANK_WIDTH, percProgressEnd),
                PROGRESS_BAR_RANK_WIDTH
            ) + ((rank - 1) * PROGRESS_BAR_RANK_WIDTH);

            const initialDelay = firstAnimationStep ? 0 : 700;
            firstAnimationStep = false;

            StartAnimation(this.layout.root, 'SeasonPassRankProgress', {
                progressFill: this.layout.ProgressBarFill,
                tractor: this.layout.Tractor,
                progress: percProgressEnd,
                progressWidth: width,
                initialDelay,
                onComplete,
            });

            StartAnimationClip(
                new DragonbonesFadeIn(this.tractorArmature, {
                    animationName: 'moving',
                    playTimes: 1,
                    fadeInTime: 200,
                })
            );
        }

        const processNextAnimation = () => {
            const target = getTarget(rank);

            if (remainingProgress >= target) {
                // Full progress bar animation for this rank
                createAnimationStep(rank, target, () => {
                    remainingProgress -= target;
                    rank++; // Move to the next rank
                    rankCompleteCallback(rank);
                    StartAnimationClip(
                        new DragonbonesFadeIn(this.tractorArmature, {
                            animationName: 'tractor_idle',
                            fadeInTime: 200,
                        })
                    );
                    if (rank < ranks.length) {
                        processNextAnimation();
                    } else {
                        completeAnimationCallback();
                        this.animating = false;
                    }
                });
            } else {
                createAnimationStep(rank, remainingProgress, () => {
                    completeAnimationCallback();
                    this.animating = false;
                    StartAnimationClip(
                        new DragonbonesFadeIn(this.tractorArmature, {
                            animationName: 'tractor_idle',
                            fadeInTime: 200,
                        })
                    );
                });
            }
        }

        processNextAnimation();
    }

    public get isAnimating(): boolean {
        return this.animating;
    }
}
